import React, { useState, useEffect } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../contexts/ThemeContext'
import { NotificationBell } from './NotificationBell'
import LanguageSelector from './LanguageSelector'
import { TelnyxCallWidget } from './TelnyxCallWidget'
import { BrokerContextSwitcher } from './BrokerContextSwitcher'
import { GlobalSearch } from './GlobalSearch'
import { makeApiCall } from '../utils/customerUtils'
import { SidebarEditor, NavigationItem } from './SidebarEditor'
import { FlowLogo } from './FlowLogo'
import {
  DashboardIcon,
  CustomersIcon,
  TasksIcon,
  CalendarIcon,
  DealsBoardIcon,
  ConversationsIcon,
  PhoneIcon,
  CalculatorIcon,
  SettingsIcon,
  MoonIcon,
  SunIcon,
  UsersIcon,
  UserIcon,
  BadgeIcon,
  ReportsIcon,
  LiveChatIcon
} from './Icons'

// Standardized page content wrapper component
interface PageContentProps {
  children: React.ReactNode
  className?: string
}

export const PageContent: React.FC<PageContentProps> = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 ${className}`}>
    {children}
  </div>
)

// Full-width wrapper for boards and full-screen features
interface FullWidthContentProps {
  children: React.ReactNode
  className?: string
}

export const FullWidthContent: React.FC<FullWidthContentProps> = ({ children, className = '' }) => (
  <div className={`w-full px-4 sm:px-6 lg:px-8 py-6 ${className}`}>
    {children}
  </div>
)

const Layout: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user, logout, isAdmin, isBroker } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isVoiceWidgetOpen, setIsVoiceWidgetOpen] = useState(false)
  const [isVoiceWidgetMinimized, setIsVoiceWidgetMinimized] = useState(false)
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null)
  const [trialStatus, setTrialStatus] = useState<any>(null)
  const [isSidebarEditorOpen, setIsSidebarEditorOpen] = useState(false)
  const [customNavigation, setCustomNavigation] = useState<NavigationItem[] | null>(null)
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(true)

  // Fetch user profile picture
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await makeApiCall('/api/users/profile')
        if (response.ok) {
          const data = await response.json()
          setProfilePictureUrl(data.user.profile_picture_url)
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
      }
    }

    if (user) {
      fetchUserProfile()
    }
  }, [user])

  // Fetch membership/trial status for brokers (trial info comes from membership endpoint)
  useEffect(() => {
    const fetchMembershipStatus = async () => {
      if (isBroker) {
        try {
          const response = await makeApiCall('/api/membership/manage/current')
          if (response.ok) {
            const data = await response.json()
            const m = data.membership
            setTrialStatus({
              is_eligible: !m.trial_used,
              is_active: m.is_trial_active && !['active', 'trialing'].includes(m.subscription_status),
              trial_used: m.trial_used,
              trial_started_at: m.trial_started_at,
              trial_ends_at: m.trial_ends_at,
              days_remaining: m.trial_days_remaining,
              has_seen_welcome_modal: m.has_seen_welcome_modal,
              has_active_subscription: ['active', 'trialing'].includes(m.subscription_status)
            })
          }
        } catch (error) {
          console.error('Failed to fetch membership status:', error)
        }
      }
    }

    if (user && isBroker) {
      fetchMembershipStatus()
    }

    const handleSubscriptionChange = () => fetchMembershipStatus()
    window.addEventListener('subscriptionChanged', handleSubscriptionChange)
    return () => window.removeEventListener('subscriptionChanged', handleSubscriptionChange)
  }, [user, isBroker])

  // All available navigation items with their metadata
  const allBrokerNavigationItems: NavigationItem[] = [
    { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: DashboardIcon, translationKey: 'navigation.dashboard' },
    { id: 'leads', name: 'Leads', href: '/dashboard/leads', icon: UserIcon, translationKey: 'navigation.leads' },
    { id: 'customers', name: 'Customers', href: '/dashboard/customers', icon: BadgeIcon, translationKey: 'navigation.customers' },
    { id: 'tasks', name: 'Tasks', href: '/dashboard/tasks', icon: TasksIcon, translationKey: 'navigation.tasks' },
    { id: 'calendar', name: 'Calendar', href: '/dashboard/calendar', icon: CalendarIcon, translationKey: 'navigation.calendar' },
    { id: 'deals', name: 'Pipeline', href: '/dashboard/pipeline', icon: DealsBoardIcon, translationKey: 'navigation.deals' },
    { id: 'calculator', name: 'Calculator', href: '/dashboard/calculator', icon: CalculatorIcon, translationKey: 'navigation.calculator' },
    { id: 'conversations', name: 'Messages', href: '/dashboard/conversations', icon: ConversationsIcon, translationKey: 'navigation.conversations' },
    { id: 'calls', name: 'Calls', href: '/dashboard/calls', icon: PhoneIcon, translationKey: 'navigation.calls' },
    { id: 'reports', name: 'Reports', href: '/dashboard/reports', icon: ReportsIcon, translationKey: 'navigation.reports' },
  ]

  const allAdminNavigationItems: NavigationItem[] = [
    { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: DashboardIcon, translationKey: 'navigation.dashboard' },
    { id: 'users', name: 'Users', href: '/dashboard/users', icon: UsersIcon, translationKey: 'navigation.users' },
    { id: 'calendar', name: 'Calendar', href: '/dashboard/calendar', icon: CalendarIcon, translationKey: 'navigation.calendar' },
    { id: 'deals', name: 'Deals', href: '/dashboard/admin/deals', icon: DealsBoardIcon, translationKey: 'navigation.deals' },
    { id: 'calculator', name: 'Calculator', href: '/dashboard/calculator', icon: CalculatorIcon, translationKey: 'navigation.calculator' },
    { id: 'reports', name: 'Reports', href: '/dashboard/reports', icon: ReportsIcon, translationKey: 'navigation.reports' },
  ]

  // Default navigation for brokers
  const defaultBrokerNavigation: NavigationItem[] = [
    allBrokerNavigationItems[0], // dashboard
    allBrokerNavigationItems[1], // leads
    allBrokerNavigationItems[2], // customers
    allBrokerNavigationItems[3], // tasks
    allBrokerNavigationItems[4], // calendar
    allBrokerNavigationItems[5], // deals
    allBrokerNavigationItems[6], // calculator
    allBrokerNavigationItems[7], // conversations
    allBrokerNavigationItems[8], // calls
  ]

  // Default navigation for admins
  const defaultAdminNavigation: NavigationItem[] = [
    allAdminNavigationItems[0], // dashboard
    allAdminNavigationItems[1], // users
    allAdminNavigationItems[2], // calendar
    allAdminNavigationItems[3], // deals
    allAdminNavigationItems[4], // calculator
  ]

  // Load sidebar preferences from database
  useEffect(() => {
    const loadSidebarPreferences = async () => {
      try {
        const response = await makeApiCall('/api/view-settings/sidebar_navigation')
        if (response.ok) {
          const data = await response.json()
          if (data.settings && data.settings.items) {
            // Map saved item IDs to full navigation items
            const allItems = isAdmin ? allAdminNavigationItems : allBrokerNavigationItems
            const savedItems = data.settings.items
              .map((id: string) => allItems.find(item => item.id === id))
              .filter((item: NavigationItem | undefined): item is NavigationItem => item !== undefined)
            
            if (savedItems.length > 0) {
              setCustomNavigation(savedItems)
            }
          }
        }
      } catch (error) {
        console.error('Failed to load sidebar preferences:', error)
      } finally {
        setIsLoadingNavigation(false)
      }
    }

    if (user) {
      loadSidebarPreferences()
    } else {
      setIsLoadingNavigation(false)
    }
  }, [user, isAdmin, isBroker])

  // Determine which navigation to use
  const navigation = customNavigation || (isAdmin ? defaultAdminNavigation : defaultBrokerNavigation)
  const availableNavigationItems = isAdmin ? allAdminNavigationItems : allBrokerNavigationItems

  const isActivePath = (path: string) => {
    // Special handling for settings to include all sub-paths
    if (path === '/dashboard/settings') {
      return location.pathname === path || location.pathname.startsWith(path + '/')
    }
    // For other paths, exact match only
    return location.pathname === path
  }

  const getRoleDisplayName = () => {
    if (isAdmin) return t('user.administrator')
    if (isBroker) return t('user.mortgageBroker')
    return t('user.user')
  }

  // Save sidebar preferences to database
  const handleSaveSidebarPreferences = async (items: NavigationItem[]) => {
    try {
      const response = await makeApiCall('/api/view-settings/sidebar_navigation', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            items: items.map(item => item.id)
          }
        })
      })

      if (response.ok) {
        setCustomNavigation(items)
        // Show success message
        console.log(t('sidebar.sidebarCustomized'))
      } else {
        console.error('Failed to save sidebar preferences')
      }
    } catch (error) {
      console.error('Error saving sidebar preferences:', error)
    }
  }

  // Reset sidebar to default
  const handleResetSidebar = async () => {
    try {
      const defaultItems = isAdmin ? defaultAdminNavigation : defaultBrokerNavigation
      await handleSaveSidebarPreferences(defaultItems)
      console.log(t('sidebar.sidebarReset'))
    } catch (error) {
      console.error('Error resetting sidebar:', error)
    }
  }

  const handleSwitchBack = () => {
    const originalToken = localStorage.getItem('original_token')
    if (originalToken) {
      localStorage.setItem('token', originalToken)
      localStorage.removeItem('original_token')
      localStorage.removeItem('is_impersonating')
      window.location.reload()
    }
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-200 relative overflow-hidden">

      {/* Status Bar - Main top element */}
      <header className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200 h-16 flex-shrink-0 relative z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Left: Logo */}
            <div className="flex items-center space-x-3">
              <FlowLogo size="md" animate={true} key={location.pathname} />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">MortgageFlow</h1>
              </div>
            </div>

            {/* Center: Global Search */}
            <div className="flex-1 max-w-2xl mx-4 hidden md:block">
              <GlobalSearch />
            </div>

            {/* Right: Broker Context Switcher, Date and Notifications - Responsive */}
            <div className="flex items-center space-x-4 lg:space-x-6">
              {/* Mobile Search Button */}
              <div className="md:hidden">
                <GlobalSearch className="w-64" />
              </div>
              
              {/* Broker Context Switcher for Collaborators */}
              <BrokerContextSwitcher />
              
              {/* Compact Trial Status Badge */}
              {trialStatus?.is_active && (
                <Link
                  to="/dashboard/settings/membership"
                  className="hidden lg:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200"
                  title={`${t('trial.freeTrial')}: ${t('trial.daysRemaining', { days: trialStatus.days_remaining })} - ${t('trial.clickToUpgrade')}`}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('trial.badge')}: {trialStatus.days_remaining}d</span>
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
              
              {/* Compact Trial Expired Badge */}
              {trialStatus?.is_expired && !trialStatus?.is_active && (
                <Link
                  to="/dashboard/settings/membership"
                  className="hidden lg:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white text-xs font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 animate-pulse"
                  title={`${t('trial.trialExpired')} - ${t('trial.clickToUpgrade')}`}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>{t('trial.upgrade')}</span>
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
              
              <NotificationBell />
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-white hidden sm:block">
                  {new Date().toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric'
                  })}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                  {new Date().toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { 
                    month: 'short', 
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Impersonation Banner */}
      {sessionStorage.getItem('is_impersonating') === 'true' && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-center font-medium flex items-center justify-center gap-4">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>{t('impersonation.warning')}</span>
          <button
            onClick={async () => {
              try {
                // Log end of impersonation on backend
                const token = localStorage.getItem('token')
                await fetch(`${(import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:5001'}/api/admin/impersonate/end`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                })
              } catch (error) {
                console.error('Failed to log impersonation end:', error)
              }

              // Switch back to original admin token
              const originalToken = sessionStorage.getItem('original_token')
              if (originalToken) {
                localStorage.setItem('token', originalToken)
                sessionStorage.removeItem('original_token')
                sessionStorage.removeItem('is_impersonating')
                window.location.href = '/dashboard/users'
              }
            }}
            className="ml-4 px-4 py-1 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            {t('impersonation.switchBack')}
          </button>
        </div>
      )}

      {/* Main content area with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Below status bar */}
        <div className={`flex-shrink-0 bg-white dark:bg-gray-800 shadow-xl transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'w-20' : 'w-64'
        }`}>
          <div className="flex flex-col h-full overflow-hidden">

          


            {/* Navigation */}
            <nav className="flex-1 py-6 px-4 space-y-2">
              {!isLoadingNavigation && navigation.map((item) => {
                const isActive = isActivePath(item.href)
                const Icon = item.icon
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ease-in-out ${
                      isActive
                        ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white shadow-lg'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                    title={isSidebarCollapsed ? t(item.translationKey) : undefined}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className={`ml-3 truncate transition-all duration-300 ease-in-out whitespace-nowrap ${
                      isSidebarCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100'
                    }`}>{t(item.translationKey)}</span>
                  </Link>
                )
              })}

              {/* Edit Sidebar Button */}
              {!isSidebarCollapsed && (
                <button
                  onClick={() => setIsSidebarEditorOpen(true)}
                  className="flex items-center px-4 py-2 w-full text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors duration-200 mt-1"
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  <span className="ml-2 truncate">{t('sidebar.editSidebar')}</span>
                </button>
              )}
            </nav>

            {/* Settings & Controls */}
            <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-4 space-y-2">
              {/* Settings Link — same for all roles, content differs via SettingsLayout */}
              <Link
                to="/dashboard/settings"
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ease-in-out ${
                  location.pathname.startsWith('/dashboard/settings')
                    ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                }`}
                title={isSidebarCollapsed ? t('navigation.settings') : undefined}
              >
                <SettingsIcon className="h-5 w-5 flex-shrink-0" />
                <span className={`ml-3 truncate transition-all duration-300 ease-in-out whitespace-nowrap ${
                  isSidebarCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100'
                }`}>{t('navigation.settings')}</span>
              </Link>

              {/* Sidebar Collapse Toggle */}
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="flex items-center px-4 py-3 w-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-xl transition-colors"
                title={isSidebarCollapsed ? t('user.expandMenu') : t('user.collapseMenu')}
              >
                <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSidebarCollapsed ? "M13 5l7 7-7 7" : "M11 19l-7-7 7-7"} />
                  </svg>
                </div>
                <span className={`ml-3 truncate transition-all duration-300 ease-in-out whitespace-nowrap ${
                  isSidebarCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100'
                }`}>{t('user.collapseMenu')}</span>
              </button>

              {/* Theme Toggle and Language Selector */}
              <div className={`flex items-center gap-4 px-4 py-2 ${
                isSidebarCollapsed ? 'justify-center flex-col gap-3' : 'justify-start'
              }`}>
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  title={theme === 'light' ? t('settings.darkMode') : t('settings.lightMode')}
                >
                  <div className="h-5 w-5 flex-shrink-0">
                    {theme === 'light' ? (
                      <MoonIcon className="h-5 w-5" />
                    ) : (
                      <SunIcon className="h-5 w-5" />
                    )}
                  </div>
                </button>

                {/* Language Selector */}
                <LanguageSelector compact={isSidebarCollapsed} />
              </div>
            </div>

            {/* User Profile */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4">
              {isSidebarCollapsed ? (
                <div className="flex flex-col items-center space-y-3">
                  <Link 
                    to="/dashboard/settings/profile"
                    className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden hover:ring-2 hover:ring-gray-900 hover:ring-offset-2 hover:ring-offset-gray-800 transition-all"
                    title={`${user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.name || 'User'} - Edit Profile`}
                  >
                    {profilePictureUrl ? (
                      <img 
                        src={profilePictureUrl} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {user?.firstName?.[0] || user?.name?.[0] || 'U'}
                        </span>
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="w-10 h-10 flex-shrink-0 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors flex items-center justify-center"
                    title={t('user.signOut')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <Link 
                    to="/dashboard/settings/profile"
                    className="flex items-center mb-4 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden group-hover:ring-2 group-hover:ring-gray-900 group-hover:ring-offset-2 group-hover:ring-offset-gray-50 dark:group-hover:ring-offset-gray-700 transition-all">
                      {profilePictureUrl ? (
                        <img 
                          src={profilePictureUrl} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {user?.firstName?.[0] || user?.name?.[0] || 'U'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
                      isSidebarCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100 ml-3'
                    }`}>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-gray-900 dark:group-hover:text-gray-600 transition-colors">
                        {user?.firstName && user?.lastName 
                          ? `${user.firstName} ${user.lastName}`
                          : user?.name || 'User'
                        }
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {getRoleDisplayName()}
                      </p>
                    </div>
                    <div className={`text-gray-400 dark:text-gray-500 group-hover:text-gray-700 transition-all duration-300 ease-in-out ${
                      isSidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100'
                    }`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors whitespace-nowrap overflow-hidden"
                  >
                    {t('user.signOut')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Content area beside sidebar */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Main Content Area */}
          <main className="flex-1 overflow-auto">
            {localStorage.getItem('is_impersonating') === 'true' && (
              <div className="bg-yellow-500 text-white p-2 text-center">
                {t('impersonating')}
                <button
                  onClick={handleSwitchBack}
                  className="ml-4"
                >
                  {t('switchBack')}
                </button>
              </div>
            )}
            <Outlet />
          </main>
        </div>
      </div>

      {/* Telnyx Call Widget - DISABLED FOR NOW */}
      {false && isBroker && (
        <>
          <TelnyxCallWidget
            isOpen={isVoiceWidgetOpen}
            isMinimized={isVoiceWidgetMinimized}
            onClose={() => {
              setIsVoiceWidgetOpen(false)
              setIsVoiceWidgetMinimized(false)
            }}
            onMinimize={() => {
              setIsVoiceWidgetOpen(false)
              setIsVoiceWidgetMinimized(true)
            }}
            onExpand={() => {
              setIsVoiceWidgetOpen(true)
              setIsVoiceWidgetMinimized(false)
            }}
          />

          {/* Voice Widget Toggle Button - Only show for brokers */}
          {!isVoiceWidgetOpen && !isVoiceWidgetMinimized && (
            <button
              onClick={() => setIsVoiceWidgetOpen(true)}
              className="fixed bottom-6 right-6 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-40"
            >
              📞
            </button>
          )}
        </>
      )}

      {/* Sidebar Editor Modal */}
      <SidebarEditor
        isOpen={isSidebarEditorOpen}
        onClose={() => setIsSidebarEditorOpen(false)}
        currentItems={navigation}
        availableItems={availableNavigationItems}
        onSave={handleSaveSidebarPreferences}
        onReset={handleResetSidebar}
      />
    </div>
  )
}

export default Layout